import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import Swal from "sweetalert2";
import api from "@/api/api.js";

/** Lo que el paquete habilita. El código pregunta por estas banderas, nunca por el nombre del paquete. */
const CAPABILITIES = [
  { key: "fiscalCertificate", label: "Constancia de situación fiscal" },
  { key: "complianceOpinion", label: "Opinión de cumplimiento (32-D)" },
  { key: "storage", label: "Almacenamiento empresarial" },
  { key: "history", label: "Historial de operaciones" },
  { key: "priorityAttention", label: "Atención prioritaria" },
];

const EDITABLE = [
  "priceCents",
  "paidFolios",
  "bonusFolios",
  "validityMonths",
  "advisories",
  "consultations",
  "active",
  ...CAPABILITIES.map((c) => c.key),
];

const pesos = (cents) => (cents / 100).toFixed(2);

/** Sólo lo que cambió: así un guardado no pisa lo que otro master editó en otro campo. */
const changes = (original, draft) =>
  Object.fromEntries(
    EDITABLE.filter((key) => draft[key] !== original[key]).map((key) => [
      key,
      draft[key],
    ]),
  );

/**
 * Configuraciones · Paquetes. El master cambia qué incluye cada paquete —precio,
 * folios, vigencia, capacidades y cupos— sin desplegar código.
 *
 * Una compra ya hecha no cambia: congeló su precio y sus folios al comprarse.
 * Lo que se edita aquí vale para las compras que vengan.
 */
const PackagesAdmin = () => {
  const [packages, setPackages] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get("/packages", { headers: { Accept: "application/json" } })
      .then(({ data }) => {
        setPackages(data);
        setDrafts(Object.fromEntries(data.map((p) => [p.id, { ...p }])));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const edit = (id, key, value) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

  const save = async (original) => {
    const payload = changes(original, drafts[original.id]);
    if (Object.keys(payload).length === 0) return;

    setSaving(original.id);
    try {
      const { data } = await api.patch(`/packages/${original.id}`, payload, {
        headers: {
          "Content-Type": "application/merge-patch+json",
          Accept: "application/json",
        },
      });
      setPackages((prev) => prev.map((p) => (p.id === data.id ? data : p)));
      setDrafts((prev) => ({ ...prev, [data.id]: { ...data } }));
      Swal.fire({
        icon: "success",
        title: "Paquete guardado",
        text: data.name,
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "No se pudo guardar",
        text:
          err?.response?.data?.["hydra:description"] ||
          err?.response?.data?.detail ||
          "Revisa los valores e intenta de nuevo.",
      });
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 p-3">
        <Spinner animation="border" size="sm" />
        <span>Cargando paquetes…</span>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">No se pudieron cargar los paquetes.</Alert>;
  }

  return (
    <div className="col">
      <p className="text-muted">
        Lo que cambies aquí vale para las compras que vengan: las que ya se
        hicieron conservan el precio y los folios con que se compraron. Un
        paquete que no está a la venta desaparece de la página pública y de la
        compra, pero quien ya lo tiene lo sigue usando.
      </p>

      {packages.map((pkg) => {
        const draft = drafts[pkg.id];
        const dirty = Object.keys(changes(pkg, draft)).length > 0;
        const unlimited = draft.validityMonths === null;
        const credited = (draft.paidFolios || 0) + (draft.bonusFolios || 0);
        const id = (field) => `pkg-${pkg.code}-${field}`;

        return (
          <Card
            key={pkg.id}
            className="mb-3"
            data-testid={`paquete-${pkg.code}`}
          >
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span className="fw-bold">
                {pkg.name}{" "}
                <small className="text-muted fw-normal">({pkg.code})</small>
              </span>
              <Form.Check
                type="switch"
                id={id("active")}
                label={draft.active ? "A la venta" : "Retirado"}
                checked={draft.active}
                onChange={(e) => edit(pkg.id, "active", e.target.checked)}
              />
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={3}>
                  <Form.Label htmlFor={id("price")}>Precio</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      id={id("price")}
                      type="number"
                      min="0"
                      step="0.01"
                      value={pesos(draft.priceCents)}
                      onChange={(e) =>
                        edit(
                          pkg.id,
                          "priceCents",
                          Math.round(Number(e.target.value || 0) * 100),
                        )
                      }
                    />
                  </InputGroup>
                </Col>
                <Col md={3}>
                  <Form.Label htmlFor={id("paid")}>Folios pagados</Form.Label>
                  <Form.Control
                    id={id("paid")}
                    type="number"
                    min="1"
                    value={draft.paidFolios}
                    onChange={(e) =>
                      edit(pkg.id, "paidFolios", Number(e.target.value))
                    }
                  />
                </Col>
                <Col md={3}>
                  <Form.Label htmlFor={id("bonus")}>
                    Folios de regalo
                  </Form.Label>
                  <Form.Control
                    id={id("bonus")}
                    type="number"
                    min="0"
                    value={draft.bonusFolios}
                    onChange={(e) =>
                      edit(pkg.id, "bonusFolios", Number(e.target.value))
                    }
                  />
                </Col>
                <Col md={3}>
                  <Form.Label htmlFor={id("validity")}>
                    Vigencia de los folios
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      id={id("validity")}
                      type="number"
                      min="1"
                      disabled={unlimited}
                      value={unlimited ? "" : draft.validityMonths}
                      onChange={(e) =>
                        edit(
                          pkg.id,
                          "validityMonths",
                          Number(e.target.value) || 1,
                        )
                      }
                    />
                    <InputGroup.Text>meses</InputGroup.Text>
                  </InputGroup>
                  <Form.Check
                    className="mt-1"
                    id={id("unlimited")}
                    label="Ilimitada"
                    checked={unlimited}
                    onChange={(e) =>
                      edit(
                        pkg.id,
                        "validityMonths",
                        e.target.checked ? null : 12,
                      )
                    }
                  />
                </Col>
              </Row>

              <div className="small text-muted mt-2">
                Se acreditan <strong>{credited}</strong> folios ·{" "}
                {credited > 0
                  ? `$${pesos(Math.round(draft.priceCents / credited))} por folio`
                  : "—"}
              </div>

              <hr />

              <Row className="g-3">
                <Col md={7}>
                  <div className="fw-semibold mb-2">Incluye</div>
                  {CAPABILITIES.map((c) => (
                    <Form.Check
                      key={c.key}
                      type="switch"
                      id={id(c.key)}
                      label={c.label}
                      checked={draft[c.key]}
                      onChange={(e) => edit(pkg.id, c.key, e.target.checked)}
                    />
                  ))}
                </Col>
                <Col md={5}>
                  <div className="fw-semibold mb-2">
                    Cupos por compra (vencen a los 12 meses)
                  </div>
                  <Row className="g-2">
                    <Col xs={6}>
                      <Form.Label htmlFor={id("advisories")}>
                        Asesorías
                      </Form.Label>
                      <Form.Control
                        id={id("advisories")}
                        type="number"
                        min="0"
                        value={draft.advisories}
                        onChange={(e) =>
                          edit(pkg.id, "advisories", Number(e.target.value))
                        }
                      />
                    </Col>
                    <Col xs={6}>
                      <Form.Label htmlFor={id("consultations")}>
                        Consultas
                      </Form.Label>
                      <Form.Control
                        id={id("consultations")}
                        type="number"
                        min="0"
                        value={draft.consultations}
                        onChange={(e) =>
                          edit(pkg.id, "consultations", Number(e.target.value))
                        }
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer className="d-flex justify-content-end align-items-center gap-2">
              {dirty && (
                <Badge bg="warning" text="dark">
                  Cambios sin guardar
                </Badge>
              )}
              <Button
                variant="outline-secondary"
                disabled={!dirty || saving === pkg.id}
                onClick={() =>
                  setDrafts((prev) => ({ ...prev, [pkg.id]: { ...pkg } }))
                }
              >
                Descartar
              </Button>
              <Button
                disabled={!dirty || saving === pkg.id}
                onClick={() => save(pkg)}
              >
                {saving === pkg.id ? "Guardando…" : "Guardar"}
              </Button>
            </Card.Footer>
          </Card>
        );
      })}
    </div>
  );
};

export default PackagesAdmin;
